import { useSession } from "@clerk/clerk-react";
import { Spinner } from "@heroui/react";
import {
  CalendarBlank,
  CaretDown,
  CaretUp,
  CheckCircle,
  Clock,
  Trash,
  Warning,
} from "@phosphor-icons/react";
import ButtonNew from "components/buttonNew/buttonNew";
import { API_URL } from "constants/index";
import { useCallback, useState } from "react";
import { toast } from "react-toastify";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import {
  PaymentPlan,
  PaymentPlanInstallment,
  PaymentPlanStatus,
  InstallmentStatus,
  PAYMENT_PLAN_STATUS_LABELS,
  PAYMENT_PLAN_STATUS_COLORS,
  PAYMENT_PLAN_FREQUENCY_LABELS,
  INSTALLMENT_STATUS_LABELS,
  INSTALLMENT_STATUS_COLORS,
} from "shared/types/payment-plan";
import { applyMask } from "shared/utils/masks";

interface PaymentPlanDetailProps {
  plan: PaymentPlan;
  onPlanUpdated: (plan: PaymentPlan) => void;
  onPlanDeleted: (planId: number) => void;
}

export const PaymentPlanDetail = ({
  plan,
  onPlanUpdated,
  onPlanDeleted,
}: PaymentPlanDetailProps) => {
  const { session } = useSession();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [updatingInstallment, setUpdatingInstallment] = useState<number | null>(null);

  const installments = plan.installments || [];
  const paidCount = installments.filter(
    (i) => i.status === InstallmentStatus.PAID
  ).length;
  const progressPercent =
    installments.length > 0 ? (paidCount / installments.length) * 100 : 0;

  // End date = due date of the last installment
  const endDate = installments.length > 0
    ? [...installments].sort((a, b) => b.installmentNumber - a.installmentNumber)[0]?.dueDate
    : null;

  const handleDelete = useCallback(async () => {
    if (!confirm("Weet je zeker dat je dit afbetalingsplan wilt verwijderen?")) {
      return;
    }

    setIsDeleting(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/rental/payment-plans/${plan.id}`,
        { method: "DELETE" },
        session
      );

      if (!response.ok) {
        throw new Error(`Failed to delete payment plan: ${response.status}`);
      }

      toast.success("Afbetalingsplan verwijderd");
      onPlanDeleted(plan.id);
    } catch (error) {
      console.error("Error deleting payment plan:", error);
      toast.error("Fout bij het verwijderen van afbetalingsplan");
    } finally {
      setIsDeleting(false);
    }
  }, [plan.id, session, onPlanDeleted]);

  const handleMarkInstallmentPaid = useCallback(
    async (installment: PaymentPlanInstallment) => {
      setUpdatingInstallment(installment.id);
      try {
        const response = await fetchWithAuth(
          `${API_URL}/rental/payment-plans/installments/${installment.id}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              status: InstallmentStatus.PAID,
              paidAmount: installment.amount,
              paidDate: new Date().toISOString().split("T")[0],
            }),
          },
          session
        );

        if (!response.ok) {
          throw new Error(`Failed to update installment: ${response.status}`);
        }

        // Refetch the full plan to get updated data
        const planResponse = await fetchWithAuth(
          `${API_URL}/rental/payment-plans/${plan.id}`,
          { method: "GET" },
          session
        );

        if (planResponse.ok) {
          const updatedPlan = await planResponse.json();
          onPlanUpdated(updatedPlan);
          toast.success("Termijn gemarkeerd als betaald");
        }
      } catch (error) {
        console.error("Error updating installment:", error);
        toast.error("Fout bij het bijwerken van termijn");
      } finally {
        setUpdatingInstallment(null);
      }
    },
    [plan.id, session, onPlanUpdated]
  );

  const handleCancelPlan = useCallback(async () => {
    if (!confirm("Weet je zeker dat je dit afbetalingsplan wilt annuleren?")) {
      return;
    }

    try {
      const response = await fetchWithAuth(
        `${API_URL}/rental/payment-plans/${plan.id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ status: PaymentPlanStatus.CANCELLED }),
        },
        session
      );

      if (!response.ok) {
        throw new Error(`Failed to cancel payment plan: ${response.status}`);
      }

      const updatedPlan = await response.json();
      onPlanUpdated(updatedPlan);
      toast.success("Afbetalingsplan geannuleerd");
    } catch (error) {
      console.error("Error cancelling payment plan:", error);
      toast.error("Fout bij het annuleren van afbetalingsplan");
    }
  }, [plan.id, session, onPlanUpdated]);

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString("nl-BE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const getInstallmentIcon = (status: InstallmentStatus) => {
    switch (status) {
      case InstallmentStatus.PAID:
        return <CheckCircle weight="fill" size={16} className="text-green-600" />;
      case InstallmentStatus.OVERDUE:
        return <Warning weight="fill" size={16} className="text-red-600" />;
      case InstallmentStatus.PARTIALLY_PAID:
        return <Clock weight="fill" size={16} className="text-orange-600" />;
      default:
        return <Clock size={16} className="text-neutral-400" />;
    }
  };

  return (
    <div className="border rounded-lg overflow-hidden">
      {/* Header - always visible */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-3 bg-white hover:bg-neutral-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <span
            className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${
              PAYMENT_PLAN_STATUS_COLORS[plan.status]
            }`}
          >
            {PAYMENT_PLAN_STATUS_LABELS[plan.status]}
          </span>
          <span className="text-sm font-medium">
            {applyMask(Number(plan.totalPlanAmount), "valuta")}
          </span>
          <span className="text-xs text-neutral-secondary-mid">
            {PAYMENT_PLAN_FREQUENCY_LABELS[plan.frequency]} · {plan.numberOfInstallments} termijnen
            {endDate && ` · tot ${formatDate(endDate)}`}
          </span>
        </div>

        <div className="flex items-center gap-3">
          {/* Mini progress */}
          <div className="flex items-center gap-1.5">
            <div className="w-16 h-1.5 bg-neutral-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-green-500 rounded-full transition-all"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
            <span className="text-xs text-neutral-secondary-mid">
              {paidCount}/{installments.length}
            </span>
          </div>

          {isExpanded ? <CaretUp size={16} /> : <CaretDown size={16} />}
        </div>
      </button>

      {/* Expanded content */}
      {isExpanded && (
        <div className="border-t p-3 flex flex-col gap-3">
          {/* Plan info */}
          <div className="grid grid-cols-3 gap-2 text-xs">
            <div>
              <span className="text-neutral-secondary-mid">Openstand bij aanmaak</span>
              <p className="font-medium">{applyMask(Number(plan.totalOutstandingAmount), "valuta")}</p>
            </div>
            <div>
              <span className="text-neutral-secondary-mid">Startdatum</span>
              <p className="font-medium">{formatDate(plan.startDate)}</p>
            </div>
            <div>
              <span className="text-neutral-secondary-mid">Aangemaakt</span>
              <p className="font-medium">{formatDate(plan.createdAt)}</p>
            </div>
          </div>

          {/* End date - standout */}
          {endDate && (
            <div className="flex items-center justify-between p-2.5 bg-moss-primary/10 border border-moss-primary/20 rounded">
              <div className="flex items-center gap-1.5">
                <CalendarBlank size={14} weight="duotone" className="text-moss-primary" />
                <span className="text-xs font-medium text-moss-primary">Einddatum</span>
              </div>
              <span className="text-sm font-semibold text-moss-primary">
                {new Date(endDate).toLocaleDateString("nl-BE", {
                  day: "2-digit",
                  month: "long",
                  year: "numeric",
                })}
              </span>
            </div>
          )}

          {plan.remarks && (
            <div className="text-xs p-2 bg-neutral-50 rounded">
              <span className="text-neutral-secondary-mid">Opmerkingen: </span>
              {plan.remarks}
            </div>
          )}

          {/* Installments list */}
          <div className="flex flex-col gap-1">
            <h6 className="text-xs font-medium text-neutral-secondary-mid flex items-center gap-1">
              <CalendarBlank size={12} />
              Termijnen
            </h6>

            {installments.map((inst) => (
              <div
                key={inst.id}
                className="flex items-center justify-between p-2 rounded hover:bg-neutral-50 group"
              >
                <div className="flex items-center gap-2">
                  {getInstallmentIcon(inst.status)}
                  <span className="text-xs text-neutral-secondary-mid">
                    Termijn {inst.installmentNumber}
                  </span>
                  <span className="text-xs">{formatDate(inst.dueDate)}</span>
                  <span
                    className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${
                      INSTALLMENT_STATUS_COLORS[inst.status]
                    }`}
                  >
                    {INSTALLMENT_STATUS_LABELS[inst.status]}
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium">
                    {applyMask(Number(inst.amount), "valuta")}
                  </span>

                  {(inst.status === InstallmentStatus.PENDING ||
                    inst.status === InstallmentStatus.OVERDUE) &&
                    plan.status === PaymentPlanStatus.ACTIVE && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMarkInstallmentPaid(inst);
                        }}
                        disabled={updatingInstallment === inst.id}
                        className="text-[11px] font-medium text-moss-primary hover:text-moss-primary/80 opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50"
                      >
                        {updatingInstallment === inst.id ? (
                          <Spinner size="sm" />
                        ) : (
                          "Markeer betaald"
                        )}
                      </button>
                    )}
                </div>
              </div>
            ))}
          </div>

          {/* Plan actions */}
          {plan.status === PaymentPlanStatus.ACTIVE && (
            <div className="flex justify-end gap-2 pt-1 border-t">
              <ButtonNew
                variant="secondaryNeutral"
                label="Annuleren"
                onClick={handleCancelPlan}
                icon={<Warning size={12} />}
              />
              <ButtonNew
                variant="secondaryNeutral"
                label={isDeleting ? "Verwijderen..." : "Verwijderen"}
                onClick={handleDelete}
                isDisabled={isDeleting}
                icon={<Trash size={12} />}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PaymentPlanDetail;
